import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  SubtarefaDefinicao,
  atualizarSubtarefa,
  criarSubtarefa,
  desativarSubtarefa,
  listarSubtarefas,
  reordenarSubtarefas,
} from '../../../api/subtarefasApi';
import { useEsteiras } from '../../../hooks/useEsteiras';
import { notify } from '../../../shared/feedback/notify';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import '../../../components/como-funciona/help-circle-btn.css';
// Esqueleto de cabeçalho compartilhado (`recepcao-header-row`, `recepcao-title`). Import
// explícito: contar com o CSS da página hospedeira é acoplamento invisível (lição do BUG-V74).
import '../../recepcao/pages/Recepcao.css';

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que é uma sub-tarefa',
    conteudo: (
      <>
        <p>
          Uma <strong>sub-tarefa</strong> é uma parte do serviço de uma esteira. Em Pintura, por
          exemplo: <strong>Lixamento</strong>, <strong>Primer</strong>, <strong>Polimento</strong>.
          Ela serve para responder, a qualquer momento, o que já foi feito no veículo — inclusive
          quando o associado liga perguntando.
        </p>
        <p>
          O catálogo é <strong>por esteira</strong>. Escolha a esteira acima e cadastre as partes do
          serviço dela.
        </p>
      </>
    ),
  },
  {
    titulo: 'Quem usa o que você cadastra aqui',
    conteudo: (
      <>
        <p>
          O <strong>Chefe de Oficina</strong> escolhe, ao planejar os serviços do veículo, quais
          sub-tarefas valem para aquele atendimento — não precisam ser todas.
        </p>
        <p>
          O <strong>colaborador</strong> marca as que terminou, uma a uma, confirmando com a senha
          dele. O que ele marca aparece para a Recepção, para o Chefe, para o setor de Eventos e na
          auditoria do veículo.
        </p>
      </>
    ),
  },
  {
    titulo: 'Enquanto não houver sub-tarefa cadastrada, nada muda',
    conteudo: (
      <p>
        Esteira sem sub-tarefa continua funcionando exatamente como antes: o planejamento não mostra
        nada a escolher e o colaborador não tem o que marcar. A configuração aqui é o que liga o
        recurso, esteira por esteira.
      </p>
    ),
  },
  {
    titulo: 'Marcar sub-tarefa não conclui o serviço',
    conteudo: (
      <>
        <p>
          Quem declara que o serviço da esteira acabou continua sendo o colaborador, ao finalizar a
          atividade, e o Chefe, ao mover o veículo. Marcar todas as sub-tarefas{' '}
          <strong>não</strong> encerra a etapa sozinho.
        </p>
        <p>
          E o contrário também vale: se o serviço for concluído com sub-tarefas sem marcar, elas{' '}
          <strong>continuam registradas como pendentes</strong> — nada é descartado, e o Chefe
          consegue ver o que ficou.
        </p>
      </>
    ),
  },
  {
    titulo: 'Ordem, edição e desativação',
    conteudo: (
      <>
        <p>
          As setas mudam a <strong>ordem</strong> em que as sub-tarefas aparecem para o Chefe e para
          o colaborador — use a ordem em que o serviço costuma ser executado.
        </p>
        <p>
          <strong>Desativar</strong> tira a sub-tarefa dos planejamentos novos, mas{' '}
          <strong>preserva</strong> o que já foi executado nos veículos: o histórico de quem fez o
          quê não é apagado. Por isso não existe exclusão definitiva.
        </p>
        <p>
          O <strong>nome</strong> pode ser editado a qualquer momento. A esteira, não: mudar a
          esteira faria o histórico dizer que alguém concluiu, numa etapa, algo que hoje pertence a
          outra. Nesse caso, desative aqui e cadastre na esteira certa.
        </p>
      </>
    ),
  },
  {
    titulo: 'O que fica registrado',
    conteudo: (
      <p>
        Toda criação, edição, reordenação e desativação feita nesta aba fica registrada com{' '}
        <strong>quem fez e quando</strong>. As marcações dos colaboradores e as escolhas do Chefe
        aparecem na <strong>linha do tempo do veículo</strong>, na tela de Auditoria.
      </p>
    ),
  },
];

/**
 * Aba "Sub-tarefas" da Administração do Sistema → Oficina (at-89, ADR 0041).
 *
 * Molde: `EspecialidadesSection` (estrutura e feedback) + a reordenação do `ChecklistAdminSection`.
 */
const SubtarefasSection: React.FC = () => {
  const { esteiras } = useEsteiras();
  const [esteiraId, setEsteiraId] = useState<string>('');
  const [itens, setItens] = useState<SubtarefaDefinicao[]>([]);
  const [carregando, setCarregando] = useState(false);
  // Separado de "lista vazia": sem isto, falha de rede vira a afirmação de negócio "esta esteira
  // não tem sub-tarefas" e o ADMIN cadastra tudo de novo (regra da at-60).
  const [cargaFalhou, setCargaFalhou] = useState(false);
  const [novoNome, setNovoNome] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [reordenando, setReordenando] = useState(false);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [nomeEditado, setNomeEditado] = useState('');
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [confirmacao, setConfirmacao] = useState<{
    titulo: string;
    mensagem: string;
    onConfirm: () => void;
  } | null>(null);

  // Só esteira de trabalho: esteira de sistema é de fluxo livre e não tem serviço a detalhar.
  const esteirasDeTrabalho = useMemo(
    () => esteiras.filter((e) => e.ativo && !e.sistema),
    [esteiras],
  );

  useEffect(() => {
    if (!esteiraId && esteirasDeTrabalho.length > 0) {
      setEsteiraId(esteirasDeTrabalho[0].id);
    }
  }, [esteirasDeTrabalho, esteiraId]);

  const carregar = useCallback(async () => {
    if (!esteiraId) return;
    setCarregando(true);
    try {
      const dados = await listarSubtarefas(esteiraId, true);
      setItens(dados);
      setCargaFalhou(false);
    } catch (error) {
      setItens([]);
      setCargaFalhou(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar as sub-tarefas desta esteira. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, [esteiraId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const ativas = useMemo(() => itens.filter((i) => i.ativa), [itens]);
  const inativas = useMemo(() => itens.filter((i) => !i.ativa), [itens]);

  const handleCriar = async () => {
    const nome = novoNome.trim();
    if (!nome || !esteiraId) return;

    setSalvando(true);
    try {
      await criarSubtarefa({ esteiraId, nome });
      setNovoNome('');
      // ⚠️ Texto à mão de propósito: `actionSuccess` conjuga no masculino singular, e
      // "sub-tarefa" é feminino — sairia "Sub-tarefa criado com sucesso" (at-65, BUG-V51).
      notify.success('Sub-tarefa criada.');
      await carregar();
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível criar a sub-tarefa.');
    } finally {
      setSalvando(false);
    }
  };

  const handleSalvarNome = async (id: string) => {
    const nome = nomeEditado.trim();
    if (!nome) return;

    setSalvando(true);
    try {
      await atualizarSubtarefa(id, { nome });
      setEditandoId(null);
      setNomeEditado('');
      notify.success('Sub-tarefa renomeada.');
      await carregar();
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível renomear a sub-tarefa.');
    } finally {
      setSalvando(false);
    }
  };

  const handleAlternarAtiva = (item: SubtarefaDefinicao) => {
    if (item.ativa) {
      setConfirmacao({
        titulo: 'Desativar sub-tarefa',
        mensagem:
          `"${item.nome}" deixa de aparecer nos planejamentos novos. O que já foi marcado nos ` +
          'veículos continua registrado — nada do histórico é apagado.',
        onConfirm: async () => {
          setConfirmacao(null);
          try {
            await desativarSubtarefa(item.id);
            notify.success('Sub-tarefa desativada.');
            await carregar();
          } catch (error) {
            notify.errorFrom(error, 'Não foi possível desativar a sub-tarefa.');
          }
        },
      });
      return;
    }

    void (async () => {
      try {
        await atualizarSubtarefa(item.id, { ativa: true });
        notify.success('Sub-tarefa reativada.');
        await carregar();
      } catch (error) {
        notify.errorFrom(error, 'Não foi possível reativar a sub-tarefa.');
      }
    })();
  };

  /**
   * Setas de ordenação. Operam só entre ATIVAS, mas o backend exige a lista completa das ativas
   * (ordem ambígua não se adivinha) — molde do reorder do checklist.
   */
  const handleMover = async (index: number, direcao: -1 | 1) => {
    const alvo = index + direcao;
    if (alvo < 0 || alvo >= ativas.length) return;

    const reordenado = [...ativas];
    [reordenado[index], reordenado[alvo]] = [reordenado[alvo], reordenado[index]];

    setReordenando(true);
    try {
      await reordenarSubtarefas(
        esteiraId,
        reordenado.map((i) => i.id),
      );
      await carregar();
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível alterar a ordem das sub-tarefas.');
    } finally {
      setReordenando(false);
    }
  };

  return (
    <div>
      <div className="admin-aba-cabecalho">
        <div className="admin-card-title">
          Sub-tarefas
          <button
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
            type="button"
          >
            ?
          </button>
        </div>
        <p className="admin-subtitle">
          Partes do serviço de cada esteira. O Chefe escolhe quais valem para o veículo, e o
          colaborador marca o que terminou.
        </p>
      </div>

      {cargaFalhou && (
        <div className="admin-card" style={{ borderLeft: '4px solid #dc2626' }}>
          <strong>Configuração não carregada.</strong>
          <p className="admin-texto-secundario">
            A lista abaixo pode estar incompleta. Recarregue a página antes de cadastrar — sem isso,
            você pode acabar criando sub-tarefas que já existem.
          </p>
        </div>
      )}

      <div className="admin-card">
        <div className="admin-card-title">Esteira</div>
        <div className="admin-form-grid">
          <div className="admin-input-group">
            <label className="admin-label" htmlFor="subtarefa-esteira">
              Escolha a esteira
            </label>
            <select
              id="subtarefa-esteira"
              className="admin-select"
              value={esteiraId}
              onChange={(e) => setEsteiraId(e.target.value)}
            >
              {esteirasDeTrabalho.map((esteira) => (
                <option key={esteira.id} value={esteira.id}>
                  {esteira.nome}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="admin-hint">
          As etapas do próprio sistema (Aguardando, Orçamento, Aguardando Entrega, Entrega) não
          aparecem aqui: elas são de fluxo livre e não têm serviço a detalhar.
        </p>
      </div>

      <div className="admin-card">
        <div className="admin-card-title">Nova sub-tarefa</div>
        <div className="admin-form-grid">
          <div className="admin-input-group">
            <label className="admin-label" htmlFor="subtarefa-nome">
              Nome
            </label>
            <input
              id="subtarefa-nome"
              className="admin-input"
              value={novoNome}
              maxLength={80}
              placeholder="Ex: Lixamento"
              onChange={(e) => setNovoNome(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleCriar();
              }}
            />
          </div>
          <div className="admin-input-group fixed-width">
            <label className="admin-label">&nbsp;</label>
            <button
              className="admin-btn admin-btn-primary"
              onClick={() => void handleCriar()}
              disabled={salvando || !novoNome.trim() || !esteiraId}
              type="button"
            >
              Adicionar
            </button>
          </div>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-title">
          Sub-tarefas desta esteira {ativas.length > 0 && `(${ativas.length})`}
        </div>

        {carregando && <p className="admin-texto-secundario">Carregando...</p>}

        {!carregando && !cargaFalhou && ativas.length === 0 && (
          <p className="admin-vazio">
            Esta esteira ainda não tem sub-tarefas. Enquanto não houver nenhuma, o planejamento e a
            tela do colaborador continuam como estão hoje.
          </p>
        )}

        {ativas.length > 0 && (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Ordem</th>
                <th>Nome</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {ativas.map((item, index) => (
                <tr key={item.id}>
                  <td>
                    <div className="admin-reorder-group">
                      <button
                        className="admin-reorder-btn"
                        title="Mover para cima"
                        aria-label="Mover para cima"
                        disabled={reordenando || index === 0}
                        onClick={() => void handleMover(index, -1)}
                        type="button"
                      >
                        ▲
                      </button>
                      <button
                        className="admin-reorder-btn"
                        title="Mover para baixo"
                        aria-label="Mover para baixo"
                        disabled={reordenando || index === ativas.length - 1}
                        onClick={() => void handleMover(index, 1)}
                        type="button"
                      >
                        ▼
                      </button>
                    </div>
                  </td>
                  <td>
                    {editandoId === item.id ? (
                      <input
                        className="admin-input"
                        value={nomeEditado}
                        maxLength={80}
                        onChange={(e) => setNomeEditado(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void handleSalvarNome(item.id);
                          if (e.key === 'Escape') setEditandoId(null);
                        }}
                        autoFocus
                      />
                    ) : (
                      item.nome
                    )}
                  </td>
                  <td>
                    {editandoId === item.id ? (
                      <>
                        <button
                          className="admin-btn admin-btn-primary"
                          onClick={() => void handleSalvarNome(item.id)}
                          disabled={salvando || !nomeEditado.trim()}
                          type="button"
                        >
                          Salvar
                        </button>
                        <button
                          className="admin-btn admin-btn-secondary"
                          onClick={() => setEditandoId(null)}
                          type="button"
                        >
                          Cancelar
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="admin-btn admin-btn-secondary"
                          onClick={() => {
                            setEditandoId(item.id);
                            setNomeEditado(item.nome);
                          }}
                          type="button"
                        >
                          Renomear
                        </button>
                        <button
                          className="admin-btn admin-btn-danger-outline"
                          onClick={() => handleAlternarAtiva(item)}
                          type="button"
                        >
                          Desativar
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {inativas.length > 0 && (
        <div className="admin-card">
          <div className="admin-card-title">Desativadas ({inativas.length})</div>
          <p className="admin-hint">
            Não aparecem em planejamentos novos. O que já foi marcado nos veículos continua
            registrado.
          </p>
          <table className="admin-table">
            <tbody>
              {inativas.map((item) => (
                <tr key={item.id}>
                  <td>
                    <span className="admin-badge-inactive">{item.nome}</span>
                  </td>
                  <td>
                    <button
                      className="admin-btn admin-btn-success-outline"
                      onClick={() => handleAlternarAtiva(item)}
                      type="button"
                    >
                      Reativar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Sub-tarefas"
        secoes={helpSections}
      />

      {confirmacao && (
        <ConfirmModal
          isOpen
          title={confirmacao.titulo}
          message={confirmacao.mensagem}
          onConfirm={confirmacao.onConfirm}
          onCancel={() => setConfirmacao(null)}
        />
      )}
    </div>
  );
};

export default SubtarefasSection;
